import * as Yup from 'yup';
import { addDays, format, isAfter, isBefore, startOfDay } from 'date-fns';
import { standaloneRentersStates, policyType, soldToEffectiveDaysIntervalByState } from '@ourbranch/lookups';
import { isFarmBasedPolicyType } from '@ourbranch/farm-utils';
import { appConfig } from 'brand-config';

// Effective date for SA Renters policies must be after October 1st, 2022
const standaloneRentersLimitDate = new Date(2022, 9, 1);
const maxDate = new Date(new Date().setDate(new Date().getDate() + 59));

const getMinPolicyDateMessage = (days) => `Policy start date must be at least ${days} days in the future`;

const schema = Yup.object().shape({
  global: Yup.object()
    .shape({
      homeEffectiveDate: Yup.date()
        .nullable()
        .test('min', function (obj) {
          if (this.options.context?.selectedOption?.includes(policyType.Home) && !this.options.context?.noBindHome) {
            const purchaseDate = this.options.context?.home?.purchaseDate;
            const purchaseDateToCompare = new Date(`${purchaseDate}T00:00`);
            const daysDateInterval = soldToEffectiveDaysIntervalByState(this.options.context.state, policyType.Home);
            const dateDaysOut = addDays(new Date(), daysDateInterval - 1);
            let message = getMinPolicyDateMessage(daysDateInterval);
            let minDate = dateDaysOut;
            const date = new Date(obj);
            if (purchaseDateToCompare && isAfter(date, dateDaysOut) && isAfter(purchaseDateToCompare, dateDaysOut)) {
              message = 'Policy start date cannot be before the home purchase date';
              minDate = new Date(purchaseDateToCompare);
            }
            if (isBefore(date, minDate)) {
              return this.createError({ message });
            }
          }
          return true;
        })
        .test('max', 'Policy start date must be within the next 60 days.', function (obj) {
          if (this.options.context?.selectedOption?.includes(policyType.Home) && !this.options.context?.noBindHome) {
            const date = new Date(obj);
            return date < maxDate;
          }
          return true;
        }),
      autoEffectiveDate: Yup.date()
        .nullable()
        .test('max', 'Policy start date must be within the next 60 days.', function (obj) {
          if (this.options.context?.selectedOption?.includes(policyType.Auto) && !this.options.context?.noBindAuto) {
            const date = new Date(obj);
            return date < maxDate;
          }
          return true;
        })
        .test('min', function (obj) {
          if (this.options.context?.selectedOption?.includes(policyType.Auto) && !this.options.context?.noBindAuto) {
            const daysDateInterval = soldToEffectiveDaysIntervalByState(this.options.context.state, policyType.Auto);
            const dateDaysOut = addDays(new Date(), daysDateInterval - 1);
            const date = new Date(obj);
            if (!isBefore(dateDaysOut, date)) {
              const message = getMinPolicyDateMessage(
                soldToEffectiveDaysIntervalByState(this.options.context.state, policyType.Auto)
              );
              return this.createError({ message });
            }
          }
          return true;
        }),
      rentersEffectiveDate: Yup.date()
        .nullable()
        .test('max', 'Policy start date must be within the next 60 days.', function (obj) {
          if (
            this.options.context?.selectedOption?.includes(policyType.Renters) &&
            standaloneRentersStates[this.options.context.state]
          ) {
            const date = new Date(obj);
            return date < maxDate;
          }
          return true;
        })
        .test('standalone-renters-min', 'Policy start date must be after 10/1/2022.', function (obj) {
          if (
            this.options.context?.selectedOption?.includes(policyType.Renters) &&
            standaloneRentersStates[this.options.context.state]
          ) {
            const date = new Date(obj);
            return date >= standaloneRentersLimitDate;
          }
          return true;
        })
        .test('min', function (obj) {
          if (
            this.options.context?.selectedOption?.includes(policyType.Renters) &&
            standaloneRentersStates[this.options.context.state]
          ) {
            const dateDaysOut = addDays(
              new Date(),
              soldToEffectiveDaysIntervalByState(this.options.context.state, policyType.Renters) - 1
            );
            const date = new Date(obj);
            if (!isBefore(dateDaysOut, date)) {
              const message = getMinPolicyDateMessage(
                soldToEffectiveDaysIntervalByState(this.options.context.state, policyType.Renters)
              );
              return this.createError({ message });
            }
          }
          return true;
        }),
      condoEffectiveDate: Yup.date()
        .nullable()
        .test('min', function (obj) {
          if (this.options.context?.selectedOption?.includes(policyType.Condo)) {
            const purchaseDate = this.options.context?.condo?.purchaseDate;
            const purchaseDateToCompare = new Date(`${purchaseDate}T00:00`);
            const dateDaysOut = addDays(
              new Date(),
              soldToEffectiveDaysIntervalByState(this.options.context.state, policyType.Condo) - 1
            );
            let message = getMinPolicyDateMessage(
              soldToEffectiveDaysIntervalByState(this.options.context.state, policyType.Condo)
            );
            let minDate = dateDaysOut;
            const date = new Date(obj);
            if (purchaseDateToCompare && isAfter(date, dateDaysOut) && isAfter(purchaseDateToCompare, dateDaysOut)) {
              message = 'Policy start date cannot be before the condo purchase date';
              minDate = new Date(purchaseDateToCompare);
            }
            if (isBefore(date, minDate)) {
              return this.createError({ message });
            }
          }
          return true;
        })
        .test('max', 'Policy start date must be within the next 60 days', function (obj) {
          if (this.options.context?.selectedOption?.includes(policyType.Condo)) {
            const date = new Date(obj);
            return date < maxDate;
          }
          return true;
        }),
      farmEffectiveDate: Yup.date()
        .nullable()
        .test('farm-min', function (obj) {
          if (!obj) {
            return true;
          }
          if (!isFarmBasedPolicyType(this.options.context?.selectedOption)) {
            return true;
          }
          const rules = appConfig?.effectiveDateRules;
          // If no rules configured or user is not an agent (i.e., underwriter), skip validation
          if (!rules || !this.options.context?.isAgency) {
            return true;
          }
          const { maxBackdateDays } = rules.agent.newBusiness;
          const minDate = startOfDay(addDays(new Date(), -maxBackdateDays));
          const date = startOfDay(new Date(obj));
          if (isBefore(date, minDate)) {
            return this.createError({
              message: `Effective date cannot be more than ${maxBackdateDays} days before today`
            });
          }
          return true;
        })
        .test('farm-max', function (obj) {
          if (!obj) {
            return true;
          }
          if (!isFarmBasedPolicyType(this.options.context?.selectedOption)) {
            return true;
          }
          const rules = appConfig?.effectiveDateRules;
          if (!rules || !this.options.context?.isAgency) {
            return true;
          }
          const { maxForwardDays } = rules.agent.newBusiness;
          const farmMaxDate = startOfDay(addDays(new Date(), maxForwardDays));
          const date = startOfDay(new Date(obj));
          if (isAfter(date, farmMaxDate)) {
            return this.createError({
              message: `Effective date must be within the next ${maxForwardDays} days`
            });
          }
          return true;
        })
        .test('farm-minimum-effective-date', function (obj) {
          if (!obj) {
            return true;
          }
          if (!isFarmBasedPolicyType(this.options.context?.selectedOption)) {
            return true;
          }
          const minDateStr = appConfig?.farmMinimumEffectiveDate;
          if (!minDateStr) {
            return true;
          }
          const minDate = startOfDay(new Date(`${minDateStr}T00:00:00`));
          const date = startOfDay(new Date(obj));
          if (isBefore(date, minDate)) {
            return this.createError({
              message: `Effective date cannot be before ${format(minDate, 'MM/dd/yyyy')}`
            });
          }
          return true;
        })
    })
    .nullable()
});

export default schema;
